/**
 * DesktopNav — centered nav links with the "Gurmat Events" dropdown group.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../context/ThemeContext';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { NAV_ITEMS, isNavActive, navLinkClasses } from './navConfig';
import NavIcon from './NavIcon';

export default function DesktopNav() {
  const [groupOpen, setGroupOpen] = useState(false);
  const location = useLocation();
  const { theme } = useTheme();
  const isSukhasan = theme === 'sukhasan';
  const groupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setGroupOpen(false);
  }, [location.pathname]);

  useClickOutside(groupRef, () => setGroupOpen(false), groupOpen);
  useEscapeKey(() => setGroupOpen(false), groupOpen);

  return (
    <nav className="hidden md:flex items-center gap-0.5 flex-1 justify-center">
      {NAV_ITEMS.map((item) => {
        if (item.kind === 'link') {
          const active = isNavActive(location.pathname, item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'relative flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition-all duration-200',
                navLinkClasses(active, isSukhasan, 'desktop'),
              )}
            >
              <NavIcon src={item.imgSrc} icon={item.icon} active={active} invert={isSukhasan} iconClass={item.iconClass} />
              {item.label}
            </Link>
          );
        }

        // Group: dropdown trigger
        const groupActive = item.children.some((c) => isNavActive(location.pathname, c.href));
        return (
          <div key={item.label} className="relative" ref={groupRef}>
            <button
              type="button"
              onClick={() => setGroupOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={groupOpen}
              className={cn(
                'relative flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition-all duration-200 focus:outline-none',
                navLinkClasses(groupActive, isSukhasan, 'desktop'),
              )}
            >
              <NavIcon src={item.imgSrc} icon={item.icon} active={groupActive} invert={isSukhasan} iconClass={item.iconClass} />
              {item.label}
              <ChevronDown
                size={13}
                className={cn(
                  'transition-transform duration-200 text-tx-muted',
                  groupOpen && 'rotate-180',
                )}
              />
            </button>

            {groupOpen && (
              <div
                role="menu"
                className="card-glass absolute left-1/2 -translate-x-1/2 top-full mt-2 z-[91] min-w-[200px] rounded-xl p-0 shadow-xl overflow-hidden animate-slideUp backdrop-blur-xl"
              >
                <div className="p-1.5">
                  {item.children.map((child) => {
                    const childActive = isNavActive(location.pathname, child.href);
                    return (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={() => setGroupOpen(false)}
                        role="menuitem"
                        className={cn(
                          'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          navLinkClasses(childActive, isSukhasan, 'menu'),
                        )}
                      >
                        <NavIcon src={child.imgSrc} icon={child.icon} active={childActive} invert={isSukhasan} iconClass={child.iconClass} />
                        {child.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
